import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {Textarea} from '@/components/ui/textarea'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {FichaDatos, type BloqueFicha} from '@/components/ui/ficha-datos'
import {DataTable, type Column} from '@/components/data-table'
import {ListCard, ListEmptyState} from '@/components/section-card'
import {
  VERIFICACIONES_PAGE_SIZE,
  useResolverVerificacion,
  useVerificaciones,
  type Verificacion,
} from '@/hooks/inci/consola/use-verificaciones'
import {publicarExito} from '@/lib/avisos'
import {translateApiError} from '@/lib/errors'
import {tope} from '@/lib/campos-texto'

const formatoFecha = new Intl.DateTimeFormat('es-CO', {year: 'numeric', month: 'short', day: 'numeric'})

const VACIO = <span className="text-muted-foreground">—</span>

/** Lo que escribió la empresa junto a la cuenta que ya existía: soporte decide comparando, no recordando (U11). */
function VerificacionDialog({verificacion, onClose}: {verificacion: Verificacion | null; onClose: () => void}) {
  const {t} = useTranslation()
  const resolucion = useResolverVerificacion()
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | null>(null)

  function escribirMotivo(valor: string) {
    setMotivo(valor)
    setError(null)
  }

  function cerrar() {
    setMotivo('')
    setError(null)
    resolucion.reset()
    onClose()
  }

  function resolver(motivoRechazo?: string) {
    if (!verificacion) return
    if (motivoRechazo !== undefined && !motivoRechazo.trim()) {
      setError(t('verificacionIdentidad.consola.motivoRequerido'))
      return
    }
    setError(null)
    resolucion.mutate(
      {id: verificacion.id, motivo: motivoRechazo?.trim()},
      {
        onSuccess: () => {
          const clave = motivoRechazo === undefined ? 'aprobada' : 'rechazada'
          publicarExito(t(`verificacionIdentidad.consola.${clave}`), verificacion.business_name, {
            testId: `verificacion-${clave}`,
          })
          cerrar()
        },
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  if (!verificacion) return null

  const bloques: BloqueFicha[] = [
    {
      clave: 'empresa',
      titulo: t('verificacionIdentidad.consola.escribioLaEmpresa', {empresa: verificacion.business_name}),
      filas: [
        {
          clave: 'cedula',
          etiqueta: t('documento.label'),
          valor: <span className="font-mono">{verificacion.cedula}</span>,
        },
        {
          clave: 'nombre',
          etiqueta: t('verificacionIdentidad.consola.nombre'),
          valor: `${verificacion.nombre_declarado} ${verificacion.apellido_declarado}`,
        },
        {
          clave: 'correo',
          etiqueta: t('verificacionIdentidad.consola.correo'),
          valor: verificacion.correo_declarado || VACIO,
        },
      ],
    },
    {
      clave: 'cuenta',
      titulo: t('verificacionIdentidad.consola.laCuenta'),
      filas: [
        {
          clave: 'nombre',
          etiqueta: t('verificacionIdentidad.consola.nombre'),
          valor: `${verificacion.nombre_cuenta} ${verificacion.apellido_cuenta}`,
        },
        {
          clave: 'correo',
          etiqueta: t('verificacionIdentidad.consola.correoVerificado'),
          valor: verificacion.correo_verificado_cuenta || VACIO,
        },
      ],
    },
  ]

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && cerrar()}>
      <DialogContent data-testid="verificacion-dialog">
        <DialogHeader>
          <DialogTitle>{t('verificacionIdentidad.consola.dialogTitle')}</DialogTitle>
        </DialogHeader>
        <FichaDatos bloques={bloques} testId="verificacion-comparacion" />
        <div className="space-y-2">
          <Label htmlFor="verificacion-motivo">{t('verificacionIdentidad.consola.motivoLabel')}</Label>
          <Textarea
            id="verificacion-motivo"
            data-testid="verificacion-motivo"
            value={motivo}
            onChange={(e) => escribirMotivo(e.target.value)}
            maxLength={tope('NOTA')}
            minRows={3}
          />
        </div>
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="text-destructive hover:text-destructive"
            data-testid="verificacion-rechazar"
            disabled={resolucion.isPending}
            onClick={() => resolver(motivo)}>
            {t('verificacionIdentidad.consola.rechazar')}
          </Button>
          <Button
            type="button"
            data-testid="verificacion-aprobar"
            disabled={resolucion.isPending}
            onClick={() => resolver()}>
            {t('verificacionIdentidad.consola.aprobar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Cuentas ya reclamadas que una empresa agregó sin identidad verificada (KAI-438); la más vieja primero. */
export function VerificacionesTab() {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const [abiertaId, setAbiertaId] = useState<string | null>(null)
  const {data, isLoading} = useVerificaciones(page)
  const filas = data?.items ?? []
  const abierta = filas.find((v) => v.id === abiertaId) ?? null

  const columns: Column<Verificacion>[] = [
    {
      key: 'empresa',
      header: t('verificacionIdentidad.consola.table.empresa'),
      cell: (v) => <CeldaEstado nombre={v.business_name} meta={formatoFecha.format(new Date(v.created_at))} />,
    },
    {
      key: 'persona',
      header: t('verificacionIdentidad.consola.table.persona'),
      cell: (v) => (
        <CeldaEstado
          nombre={`${v.nombre_declarado} ${v.apellido_declarado}`}
          meta={<span className="font-mono">{v.cedula}</span>}
        />
      ),
    },
    {
      key: 'cuenta',
      header: t('verificacionIdentidad.consola.table.cuenta'),
      cell: (v) => (
        <CeldaEstado
          nombre={`${v.nombre_cuenta} ${v.apellido_cuenta}`}
          meta={v.correo_verificado_cuenta ?? t('verificacionIdentidad.consola.sinCorreoVerificado')}
        />
      ),
    },
  ]

  if (isLoading && !data) return <Skeleton className="h-64 w-full" />

  return (
    <>
      <ListCard
        footer={
          !!data?.total && (
            <PaginationBar page={page} pageSize={VERIFICACIONES_PAGE_SIZE} total={data.total} onPageChange={setPage} />
          )
        }>
        <DataTable
          columns={columns}
          rows={filas}
          rowKey={(v) => v.id}
          onRowClick={(v) => setAbiertaId(v.id)}
          emptyState={<ListEmptyState page title={t('verificacionIdentidad.consola.empty')} />}
        />
      </ListCard>
      <VerificacionDialog verificacion={abierta} onClose={() => setAbiertaId(null)} />
    </>
  )
}
